import { defineStore } from 'pinia'
import { getData, Host, postData, putData } from '@/utils/api'
import {
  type ConfirmationResult,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  getIdToken,
  signInWithEmailAndPassword
} from 'firebase/auth'
import { auth } from '@/plugins/firebaseConfig'
import type { RegisterUser, ValidateUser } from '@/models/user'

// Definimos el store de usuario
export const useAuthStore = defineStore('auth', {
  state: () => ({
    userAuth: <any>{},
    confirmationResult: <ConfirmationResult>{},
    parameters: [
      { type: 'text', text: '' },
      { type: 'text', text: '' },
      { type: 'text', text: '' },
      { type: 'text', text: '' }
    ] as Array<{ type: string; text: string }>
  }),
  actions: {
    async validateInfoUser(registerUser: ValidateUser) {
      const response = await postData('/auth/validate', registerUser, Host.AUTH)

      if (!response.success) {
        console.error(response.message)
        return
      }

      return response.query
    },
    async createAuth(userAuth: RegisterUser) {
      const response = await postData('/auth/signup', userAuth, Host.AUTH)
      console.log('response login', response)
      if (!response.success) {
        console.error(response.message)
        return
      }
      if (response.query.token) {
        this.userAuth = response.query.user
        localStorage.setItem('wepago-token', response.query.token)
      }
      return response.query.user
    },
    async getAuth() {
      const response = await getData(`/auth/${this.userAuth.uid}`)
      // console.log('response', response)
      if (response.data || response.query) {
        this.userAuth = response.data || response.query.user
        this.parameters[0].text = this.userAuth.email
        this.deviceType()
      }

      console.log('alerta', response.message)
    },
    async updateAuth(userAuth: any) {
      const resp = await putData(`/auth/${this.userAuth.uid}`, userAuth)
      console.log('resp updateAuth', resp)
    },
    async encryptPassword(password: string) {
      const { encrypted: encryptedPass } = await getData(`/encrypt/${password}`)
      return encryptedPass
    },
    async signInEmail(target: string, password: string) {
      try {
        const response = await postData(
          `/auth/login`,
          { target, password, channel: 'PASSWORD' },
          Host.AUTH
        )

        if (response.query.token) {
          this.userAuth = response.query.user
          localStorage.setItem('wepago-token', response.query.token)
        }
      } catch (error) {
        return error
      }
    },
    async passwordRecovery(target: string, password: string) {
      try {
        const response = await postData(
          `/auth/password-recovery`,
          { target, channel: 'EMAIL', password },
          Host.AUTH
        )
        return response.query
      } catch (error) {
        console.error('Error passwordRecovery:', error)
        return { success: false }
      }
    },

    async verifyEmail(email: string) {
      const response = await getData(`/auth/uniqueness/email/${email}`, Host.AUTH)
      return response
    },

    async verifyPhone(phone: string) {
      const response = await getData(`/auth/uniqueness/phone/${phone}`, Host.AUTH)
      return response.query?.isValid ? true : false
    },
    async signInPhone(
      state: 'sendCode' | 'verifyCode',
      { phone, elementId, code }: { phone?: string; elementId?: string; code?: string }
    ) {
      switch (state) {
        case 'sendCode':
          await this.sendCode(phone!, elementId || 'recaptcha-container')
          break
        case 'verifyCode':
          await this.verifyCode(code!)
          break
      }
    },

    deviceType() {
      const ua = navigator.userAgent
      const devices = ['Android', 'iPhone', 'iPad', 'Mac', 'Windows', 'Linux']
      const device = devices.find((d) => ua.includes(d)) || 'Otro'
      // Verificación antes de asignar
      if (this.parameters && this.parameters[3]) {
        this.parameters[3].text = device
      }
    },
    setupRecaptcha(elementId: string) {
      // @ts-ignore
      window.recaptchaVerifier = new RecaptchaVerifier(auth, elementId, {
        size: 'invisible',
        'expired-callback': function () {
          // reCAPTCHA expiró, reiniciar
          this.setupRecaptcha.clear()
        }
      })
      // @ts-ignore
      return window.recaptchaVerifier
    },
    async sendCode(phoneNumber: string, elementId: string) {
      try {
        // @ts-ignore
        if (typeof window.recaptchaVerifier === 'undefined') this.setupRecaptcha(elementId)
        // @ts-ignore
        const appVerifier = window.recaptchaVerifier
        this.confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, appVerifier)
      } catch (error: any) {
        console.error(error)
      }
    },
    async verifyCode(code: string) {
      try {
        // @ts-ignore
        const { user } = await this.confirmationResult.confirm(code)

        if (!user.uid) {
          console.log('alerta', 'No se pudo iniciar sesión, intente de nuevo')
          window.location.reload()
          return
        }

        this.userAuth = user
        this.confirmationResult = <ConfirmationResult>{}
      } catch (error) {
        console.error(error)
      }
    },
    async onSignOut() {
      this.userAuth = {}
      localStorage.clear()
      await auth.signOut()
    },
    async getAuthToken() {
      return await getIdToken(this.userAuth)
    },
    async checkAuth() {
      //Verificamos primero la autentificación por OTP
      const token = localStorage.getItem('wepago-token')

      return await new Promise((resolve) => {
        if (token) return resolve(true)
        resolve(false)
      })
    },

    async loginWithOtp({
      target,
      channel,
      code
    }: {
      target: string
      channel: 'WHATSAPP' | 'EMAIL'
      code: string
    }) {
      try {
        const response = await postData('/auth/login', { target, channel, code }, Host.AUTH)
        console.log('response login', response)
        if (response.query.token) {
          this.userAuth = response.query.user
          localStorage.setItem('wepago-token', response.query.token)
        }
        return response
      } catch (error) {
        console.error(error)
      }
    },

    async sendOtpCode({ target, channel }: { target: string; channel: 'WHATSAPP' | 'EMAIL' }) {
      try {
        const response = await postData(
          '/auth/otp',
          {
            target: target.toLowerCase(),
            channel
          },
          Host.AUTH
        )
        return response
      } catch (error) {
        console.error('Error sending OTP:', error)
        return { success: false }
      }
    },

    async verifyOtpCode(code: string, target: string) {
      try {
        const response = await postData(
          '/auth/verify-otp',
          {
            code,
            target: target.toLowerCase(),
            channel: 'EMAIL'
          },
          Host.AUTH
        )
        return response
      } catch (error) {
        console.error('Error verifying OTP:', error)
        return { success: false }
      }
    },

    async updatePassword(password: string, target: string) {
      try {
        const response = await postData(
          '/auth/password-recovery',
          {
            password,
            target: target.toLowerCase(),
            channel: 'EMAIL'
          },
          Host.AUTH
        )
        return response
      } catch (error) {
        console.error('Error updating password:', error)
        return { success: false }
      }
    }
  }
})
